<script lang="ts" setup>
import {
  computed,
  inject,
  nextTick,
  onMounted,
  provide,
  ref,
  useAttrs,
  watch,
} from 'vue'
import { Listbox } from '@headlessui/vue'
import type { FieldProvider } from '../../types/field'
import { DTFieldProvider, DTFieldSelectProvider } from '../../keys'
import type { ComplexClassName } from '../../types/common'
import { classArray } from '../../utils/class'
import type { DTSelectModelValue, DTSelectOption, FieldInputSizes } from './types'

const props = withDefaults(defineProps<{
  modelValue?: DTSelectModelValue
  disabled?: boolean
  sizeVariant?: keyof typeof FieldInputSizes
  invalid?: boolean
  id?: string
  required?: boolean
  multiple?: boolean
  options?: DTSelectOption[]
  placeholder?: string
  wrapperClass?: ComplexClassName
}>(), {
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', event: DTSelectModelValue): void
}>()

const attrs = useAttrs()
const touched = ref(false)

const labelProvider = inject<FieldProvider | undefined>(DTFieldProvider, undefined)
const inputId = computed(() => props?.id ?? labelProvider?.inputId)
const descriptionId = computed(() => labelProvider?.descriptionId)
const nativeSelect = ref<HTMLSelectElement>()
const registeredOptions = ref<DTSelectOption[]>([])
const fieldSelectBtn = ref<HTMLButtonElement>()

watch(() => props.required, (required) => {
  if (labelProvider)
    labelProvider.hasRequiredInput = required
}, { immediate: true })

const boundAttrs = computed(() =>
  ({
    ...attrs,
    'disabled': props.disabled,
    'id': inputId.value,
    'aria-describedby': descriptionId.value,
  }),
)

onMounted(() => {
  nextTick(() => {
    /**
     * Firefox seems to persist select value through "plain" refresh.
     * This hack is used to override it.
     */
    if (!props.modelValue && nativeSelect.value)
      nativeSelect.value.value = ''
  })
})

provide(DTFieldSelectProvider, {
  registerOption: (opt) => {
    registeredOptions.value.push(opt)
  },
  registerFieldSelectButton: (btn) => {
    fieldSelectBtn.value = btn
  },
  selectButton: () => fieldSelectBtn.value as HTMLButtonElement,
})

const isValidSelection = computed(() => Boolean(
  Array.isArray(props.modelValue)
    ? props.modelValue.length
    : (props.modelValue || props.modelValue === null),
))

const wrapperClass = computed<string[]>(() => {
  const cls = [
    'relative',
    'bg-white',
    'min-w-0',
    'grow',
    'inline-flex',
    'w-auto',
    'rounded-lg',
    'border-solid',
    'border',
    'focus-within:border-gray-800',
    'antialiased',
  ]
  if (props.disabled)
    cls.push('border-gray-300/40')

  else
    cls.push('border-gray-300')

  if (props.invalid)
    cls.push('border-red-300 focus:border-red-500')

  if (touched.value && !isValidSelection.value && props.required) {
    cls.push(
      'invalid:[&:not(:focus)]:border-red-300 invalid:focus:border-red-500',
    )
  }
  return cls.concat(classArray(props.wrapperClass ?? ''))
})

const selection = computed(() => (props.options ?? registeredOptions.value)
  ?.filter((option) => {
    if (Array.isArray(props.modelValue))
      return (props.modelValue as any[]).includes(option.value)

    return props.modelValue === option.value
  }))

const fieldset = ref<HTMLFieldSetElement>()

const focusout = (ev: FocusEvent) => {
  if (fieldset.value?.contains(ev.relatedTarget as Node))
    return

  touched.value = true
}
</script>

<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <fieldset
    ref="fieldset"
    class="dt-field-select"
    :class="wrapperClass"
    @focusout="focusout"
  >
    <!-- eslint-enable tailwindcss/no-custom-classname -->
    <select
      ref="nativeSelect"
      class="absolute inset-0 size-full overflow-hidden opacity-0"
      :required="required"
      :value="isValidSelection"
      :disabled="disabled"
      tabindex="-1"
      @focus="fieldSelectBtn?.focus()"
    >
      <option
        v-if="!isValidSelection"
        selected
        value=""
      >
        Choose option
      </option>
      <option
        v-else
        :value="isValidSelection"
        selected
      />
    </select>

    <Listbox
      v-slot="{ open }"
      class="relative w-full"
      as="div"
      :multiple="multiple"
      :model-value="modelValue"
      v-bind="boundAttrs"
      @update:model-value="(value: DTSelectModelValue) => emit('update:modelValue', value)"
    >
      <slot
        :open="open"
        :selection="selection"
      >
        <DTFieldSelectButton
          :value="selection"
          :placeholder="placeholder"
          :disabled="disabled"
          :size-variant="sizeVariant"
        />
        <DTFieldSelectOptions>
          <DTFieldSelectOption
            v-for="option, index in options"
            :key="index"
            :value="option.value"
            :text="option.text"
          />
        </DTFieldSelectOptions>
      </slot>
    </listbox>
  </fieldset>
</template>
